import { ReferencedEntry } from 'contentful';
import { get } from 'src/utils/EntryUtil';
import { FlatSysV2 } from './typesUtil';

interface ReferencedInputEntry extends ReferencedEntry<InputEntry>{}

type PartialRecord<K extends keyof any, T> = {
  [P in K]?: T;
};

type InputEntry = PartialRecord<string, ReferencedInputEntry | ReferencedInputEntry[] | object | Primitive>;

type ExtractFields<T extends ReferencedInputEntry> = Pick<Required<T>, 'fields'>['fields'];

type ArrayFields<T extends ReadonlyArray<ReferencedInputEntry>> = T[number];

type ObjectFlatEntry<T extends ReferencedInputEntry, F = ExtractFields<T>> = F extends never ? undefined : FlatEntry<F>;

type ArrayFlatEntry<T extends ReadonlyArray<ReferencedInputEntry>> = Array<ObjectFlatEntry<ArrayFields<T>>>;

type FlatFields<T extends InputEntry, R = Required<T>> = {[K in keyof R]: R[K] extends ReferencedInputEntry ? ObjectFlatEntry<R[K]> : R[K] extends ReadonlyArray<ReferencedInputEntry> ? ArrayFlatEntry<R[K]> : R[K]};

export type FlatEntry<T extends InputEntry> = FlatSysV2 & Partial<FlatFields<T>>;

/**
 * Type guard that asserts if entry is a referenced entry from Contentful
 * @param entry entry from Contentful
 */
export function isReferencedInputEntry (entry: InputEntry[keyof InputEntry]): entry is ReferencedInputEntry {
  return Boolean(entry && typeof entry === 'object' && get(entry as any, 'fields'));
}

/**
 * Function to flatten Sys object from a Contentful entry
 * @param entry The Contentful entry
 * @returns Object containing id and contentType of the entry, or undefined
 */
function flattenContentfulSys<T extends InputEntry> (entry: ReferencedEntry<T>): FlatSysV2 | undefined {
  const sys = get(entry, 'sys');
  if (!sys) return;

  const { id, contentType } = sys;
  const contentTypeId = contentType?.sys?.id;

  return {
    id,
    contentType: contentTypeId,
  };
}

/**
 * Function flatten Fields object from a contentful entry
 * @param entry The Contentful entry
 * @returns Flattened fields object that consist of:
 * All non-object Values, as is
 * Flattened objects of type FlatEntry
 * Arrays containing flattened objects of type FlatEntry
 */
function flattenContentfulFields<T extends InputEntry>(entry: ReferencedEntry<T>) {
  const fields = get(entry, 'fields');
  // early return if fields doesn't exist or is an empty object
  if (!fields || Object.keys(fields).length === 0) return;

  const flatFields = (Object.keys(fields) as [keyof T]).reduce((prev, curr) => {
    const value = fields[curr];
    if (Array.isArray(value) && value.some(val => isReferencedInputEntry(val))) {
      prev[curr] = value.map(item => flattenContentfulData(item)).filter(Boolean);
    } else if (isReferencedInputEntry(value)) {
      prev[curr] = flattenContentfulData(value);
    } else {
      prev[curr] = value;
    }
    return prev;
  }, {} as PartialRecord<keyof T, FlatEntry<T> | Array<FlatEntry<T>> | Primitive | object>) as FlatFields<T>;

  return flatFields;
}

/**
 * Function to flatten a contentful entry
 * @param entry The contentful entry
 * @returns Flattened entry consisting of:
 * Flattened Sys object
 * Flattened Fields object
 */
export function flattenContentfulData<T extends InputEntry>(entry?: ReferencedEntry<T>): FlatEntry<T> | undefined {
  if (!entry) return;

  const flatSys = flattenContentfulSys(entry);
  const flatFields = flattenContentfulFields(entry);

  if (!flatFields || !flatSys) return;

  return {
    ...flatSys,
    ...flatFields,
  };
}
